import { useEffect, useRef, useCallback } from 'react'
import { generateId } from '../lib/utils.js'
import type { WsMessage, WsInbound } from '../types/index.js'

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * URL de base du WebSocket.
 *
 * Priorité :
 *   1. `VITE_WS_URL` (URL absolue type `ws://host:port` ou `wss://host`)
 *   2. Construction dynamique à partir de `window.location` — le schéma `ws`
 *      ou `wss` suit automatiquement http/https de la page. Indispensable pour
 *      que l'app fonctionne aussi bien en local qu'à travers un tunnel HTTPS
 *      (Cloudflare, ngrok…) ou un reverse proxy en production.
 */
function resolveWsBase(): string {
  const envVal = (import.meta.env.VITE_WS_URL ?? '').toString().trim()
  if (envVal) return envVal.replace(/\/$/, '')
  if (typeof window === 'undefined') return 'ws://localhost:3000'
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}`
}

const WS_BASE = resolveWsBase()

/** Première tentative presque immédiate : un redémarrage d'API dure quelques secondes. */
const RECONNECT_MIN_DELAY_MS = 1_000
/**
 * Plafond volontairement bas. Une console d'antenne qui repasse en ligne trente
 * secondes après le retour du backend, c'est acceptable ; une minute, non.
 */
const RECONNECT_MAX_DELAY_MS = 20_000
const PING_INTERVAL_MS       = 25_000
/**
 * Le serveur envoie un PING toutes les 30 s. Au-delà de deux silences, la
 * socket est réputée morte même si le navigateur la croit ouverte : c'est le
 * cas d'un TCP coupé sans FIN — VPN qui bascule, NAT qui oublie la session,
 * tunnel qui se ferme. Sans cette surveillance, `onclose` n'arrive jamais et la
 * console reste aveugle en affichant « connecté ».
 */
const SILENCE_TIMEOUT_MS = 70_000
const SILENCE_CHECK_MS   = 10_000

/**
 * Temporisation avant la prochaine tentative : exponentielle, plafonnée, avec
 * une gigue de ±20 % pour qu'au retour d'un backend, vingt consoles ne frappent
 * pas à la même milliseconde.
 *
 * Aucun paramètre ne permet d'y exprimer un abandon : quel que soit le nombre
 * d'essais, cette fonction rend un délai fini. C'est la garantie que la boucle
 * ne peut pas s'arrêter d'elle-même.
 */
export function reconnectDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(RECONNECT_MIN_DELAY_MS * 2 ** Math.max(0, attempt), RECONNECT_MAX_DELAY_MS)
  return Math.round(base * (0.8 + random() * 0.4))
}

export type WsStatus = 'idle' | 'connecting' | 'open' | 'reconnecting'

export interface UseWebSocketOptions {
  channelId:  string
  onMessage:  (msg: WsMessage) => void
  onConnect?:    () => void
  onDisconnect?: () => void
  enabled?:   boolean   // false = ne pas connecter
  /** Suit chaque changement d'état, pour l'afficher dans la barre d'antenne. */
  onStatusChange?: (status: WsStatus, attempts: number) => void
  /** Code de fermeture brut, pour distinguer coupure réseau et session refusée. */
  onClose?: (code: number, reason: string) => void
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * WebSocket de la console d'antenne.
 *
 * Règle unique et non négociable : **la boucle de reconnexion ne s'arrête
 * jamais** tant que le hook est monté. Une console de diffusion qui renonce
 * après quelques essais laisse l'opérateur devant une page qui a l'air vivante
 * — l'horloge tourne, la grille s'affiche — alors qu'elle ne reçoit plus rien
 * de l'antenne. C'est précisément ce qui s'est produit après un redémarrage
 * d'API : dix tentatives, un `console.error`, et plus jamais rien.
 *
 * Trois mécanismes concourent au même but :
 *
 * - **une temporisation exponentielle plafonnée** (1 s → 20 s, avec gigue) qui
 *   ne compte pas ses essais et ne rend jamais la main ;
 * - **des réveils sur événement** — retour du réseau, retour d'onglet, retour
 *   de page — qui court-circuitent l'attente : au retour du VPN, la reprise est
 *   immédiate, pas dans vingt secondes ;
 * - **une surveillance du silence**, seule protection contre la socket
 *   fantôme que le navigateur croit ouverte.
 */
export function usePlayoutWebSocket({
  channelId,
  onMessage,
  onConnect,
  onDisconnect,
  enabled = true,
  onStatusChange,
  onClose,
}: UseWebSocketOptions) {
  const wsRef          = useRef<WebSocket | null>(null)
  const attemptsRef    = useRef(0)
  const retryTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef   = useRef<ReturnType<typeof setInterval> | null>(null)
  const silenceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastMessageRef = useRef(0)
  const mountedRef     = useRef(true)
  const statusRef      = useRef<WsStatus>('idle')

  // Les rappels passent par des refs : l'identité de `connect` doit rester
  // stable. Une identité qui change relance l'effet, dont le nettoyage annule
  // la reconnexion en attente — c'est une des façons dont la boucle mourait en
  // silence, sans même une tentative visible côté serveur.
  const onMessageRef    = useRef(onMessage)
  const onConnectRef    = useRef(onConnect)
  const onDisconnectRef = useRef(onDisconnect)
  const onStatusRef     = useRef(onStatusChange)
  const onCloseRef      = useRef(onClose)
  onMessageRef.current    = onMessage
  onConnectRef.current    = onConnect
  onDisconnectRef.current = onDisconnect
  onStatusRef.current     = onStatusChange
  onCloseRef.current      = onClose

  const channelRef = useRef(channelId)
  const enabledRef = useRef(enabled)
  channelRef.current = channelId
  enabledRef.current = enabled

  const setStatus = useCallback((status: WsStatus) => {
    if (statusRef.current === status) return
    statusRef.current = status
    onStatusRef.current?.(status, attemptsRef.current)
  }, [])

  const clearTimers = useCallback(() => {
    if (retryTimerRef.current)   { clearTimeout(retryTimerRef.current);   retryTimerRef.current = null }
    if (pingTimerRef.current)    { clearInterval(pingTimerRef.current);   pingTimerRef.current = null }
    if (silenceTimerRef.current) { clearInterval(silenceTimerRef.current); silenceTimerRef.current = null }
  }, [])

  const connect = useCallback((immediate = false) => {
    if (!mountedRef.current || !enabledRef.current || !channelRef.current) return
    const existing = wsRef.current
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) return

    if (immediate && retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    setStatus(attemptsRef.current === 0 ? 'connecting' : 'reconnecting')

    const socketChannelId = channelRef.current
    const url = `${WS_BASE}/ws/playout/${socketChannelId}`
    const ws  = new WebSocket(url)
    wsRef.current = ws

    /** Une socket périmée ne doit plus rien piloter : elle a déjà un successeur. */
    const isCurrent = () => wsRef.current === ws

    const scheduleReconnect = () => {
      if (!mountedRef.current || !enabledRef.current) return
      if (retryTimerRef.current) return
      const delay = reconnectDelayMs(attemptsRef.current)
      attemptsRef.current++
      setStatus('reconnecting')
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null
        connect()
      }, delay)
    }

    ws.onopen = () => {
      if (!isCurrent() || socketChannelId !== channelRef.current) { ws.close(); return }
      attemptsRef.current = 0
      lastMessageRef.current = Date.now()
      setStatus('open')
      onConnectRef.current?.()

      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: generateId(), type: 'PING' }))
        }
      }, PING_INTERVAL_MS)

      silenceTimerRef.current = setInterval(() => {
        if (!isCurrent() || ws.readyState !== WebSocket.OPEN) return
        if (Date.now() - lastMessageRef.current <= SILENCE_TIMEOUT_MS) return
        console.warn('[WS] Silence prolongé — socket réputée morte, reconnexion')
        // `close()` déclenche `onclose`, donc la reconnexion habituelle.
        ws.close()
      }, SILENCE_CHECK_MS)
    }

    ws.onmessage = (event) => {
      // Un message de l'ancienne chaîne reçu entre le rendu React et le
      // nettoyage de l'effet ne doit jamais valider la nouvelle chaîne.
      if (!isCurrent() || socketChannelId !== channelRef.current) return
      lastMessageRef.current = Date.now()
      try {
        const msg = JSON.parse(event.data) as WsMessage
        // On ignore les PING côté client — le serveur gère le keepalive
        if (msg.type === 'PING') return
        onMessageRef.current(msg)
      } catch {
        console.warn('[WS] Message non-JSON reçu:', event.data)
      }
    }

    ws.onclose = (event) => {
      if (pingTimerRef.current)    { clearInterval(pingTimerRef.current);    pingTimerRef.current = null }
      if (silenceTimerRef.current) { clearInterval(silenceTimerRef.current); silenceTimerRef.current = null }
      if (!isCurrent()) return
      wsRef.current = null
      // Sans le code, une coupure de tunnel (1006) et une session invalidée
      // (4401) sont indiscernables : la console affiche la même page d'attente
      // et l'opérateur n'a rien à rapporter. On le trace toujours.
      console.warn(
        `[WS] Fermeture code=${event.code} reason="${event.reason}" propre=${event.wasClean} — reconnexion`,
      )
      onDisconnectRef.current?.()
      onCloseRef.current?.(event.code, event.reason)
      scheduleReconnect()
    }

    ws.onerror = () => {
      // L'événement `close` suivra automatiquement, qui gérera la reconnexion
    }
  }, [setStatus])

  const send = useCallback((msg: WsInbound) => {
    const ws = wsRef.current
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg))
    }
  }, [])

  /** Reprise immédiate, sans attendre la temporisation en cours. */
  const reconnect = useCallback(() => {
    attemptsRef.current = 0
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) return
    if (ws) { wsRef.current = null; ws.close() }
    connect(true)
  }, [connect])

  const disconnect = useCallback(() => {
    const wasActive = statusRef.current !== 'idle'
    clearTimers()
    const ws = wsRef.current
    wsRef.current = null
    ws?.close()
    setStatus('idle')
    if (wasActive) onDisconnectRef.current?.()
  }, [clearTimers, setStatus])

  useEffect(() => {
    mountedRef.current = true
    attemptsRef.current = 0
    if (enabled && channelId) connect(true)

    return () => {
      mountedRef.current = false
      disconnect()
    }
  }, [channelId, enabled, connect, disconnect])

  // Réveils : le navigateur sait avant nous que le réseau est revenu ou que
  // l'onglet redevient visible. Les attendre plutôt que le prochain timer, ce
  // sont des dizaines de secondes d'aveuglement en moins — et sur un onglet
  // d'arrière-plan gelé par le navigateur, c'est le seul réveil possible.
  useEffect(() => {
    if (!enabled || !channelId) return
    const wake = () => {
      if (document.visibilityState === 'hidden') return
      if (wsRef.current?.readyState === WebSocket.OPEN) return
      attemptsRef.current = 0
      connect(true)
    }
    window.addEventListener('online', wake)
    window.addEventListener('focus', wake)
    window.addEventListener('pageshow', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      window.removeEventListener('online', wake)
      window.removeEventListener('focus', wake)
      window.removeEventListener('pageshow', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [channelId, connect, enabled])

  return { send, disconnect, reconnect }
}
